import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  X,
  CheckCircle,
  Briefcase,
  Calendar,
  Code,
  SlidersHorizontal,
  ArrowUpDown,
} from 'lucide-react';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { firebaseApp } from '../../firebase.js';
import { useAuth } from '../../contexts/AuthContext.jsx';

const CandidateFilters = ({
  onFilterChange,
  candidatesCount,
  sortBy,
  sortDirection,
  onSortChange,
}) => {
  const { user } = useAuth();
  const db = getFirestore(firebaseApp);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJob, setSelectedJob] = useState('all');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [experienceLevel, setExperienceLevel] = useState('all');
  const [verificationStatus, setVerificationStatus] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [skillInput, setSkillInput] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  // Fetch HR's jobs
  useEffect(() => {
    if (!user) return;

    const fetchJobs = async () => {
      try {
        const jobsQuery = query(
          collection(db, 'hrJobs'),
          where('hrId', '==', user.id)
        );

        const snapshot = await getDocs(jobsQuery);
        const jobsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setJobs(jobsList);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setLoadingJobs(false);
      }
    };

    fetchJobs();
  }, [user, db]);

  // Update filters whenever any filter changes
  useEffect(() => {
    const filters = {
      searchTerm: searchTerm.trim(),
      jobId: selectedJob !== 'all' ? selectedJob : undefined,
      skills: selectedSkills.length > 0 ? selectedSkills : undefined,
      experienceLevel: experienceLevel !== 'all' ? experienceLevel : undefined,
      verificationStatus,
      dateRange: null,
    };

    // Handle date range
    if (dateRange === '7days') {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      filters.dateRange = { start, end };
    } else if (dateRange === '30days') {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      filters.dateRange = { start, end };
    } else if (dateRange === 'custom' && customStartDate && customEndDate) {
      filters.dateRange = {
        start: new Date(customStartDate),
        end: new Date(customEndDate),
      };
    }

    onFilterChange(filters);
  }, [
    searchTerm,
    selectedJob,
    selectedSkills,
    experienceLevel,
    verificationStatus,
    dateRange,
    customStartDate,
    customEndDate,
  ]);

  const handleAddSkill = () => {
    if (skillInput.trim() && !selectedSkills.includes(skillInput.trim())) {
      setSelectedSkills([...selectedSkills, skillInput.trim()]);
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skill) => {
    setSelectedSkills(selectedSkills.filter((s) => s !== skill));
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedJob('all');
    setSelectedSkills([]);
    setExperienceLevel('all');
    setVerificationStatus('all');
    setDateRange('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setSkillInput('');
  };

  const activeFiltersCount =
    (searchTerm ? 1 : 0) +
    (selectedJob !== 'all' ? 1 : 0) +
    (selectedSkills.length > 0 ? 1 : 0) +
    (experienceLevel !== 'all' ? 1 : 0) +
    (verificationStatus !== 'all' ? 1 : 0) +
    (dateRange !== 'all' ? 1 : 0);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 w-full">
      {/* Top Bar: Search & Primary Filters */}
      <div className="flex flex-col lg:flex-row items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 w-full lg:w-auto">
          <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search candidates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2.5 w-full border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-500 bg-gray-50/50 transition-all text-sm"
          />
        </div>

        {/* Job Filter - Compact */}
        <div className="w-full lg:w-64">
          <div className="relative">
            <Briefcase className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <select
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              className="w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-red-500/20 focus:border-red-500 text-sm appearance-none cursor-pointer"
            >
              <option value="all">All Jobs</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>{job.jobTitle}</option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </div>
          </div>
        </div>

        {/* Sort Filter - Compact */}
        <div className="w-full lg:w-48">
          <div className="relative">
            <ArrowUpDown className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <select
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value)}
              className="w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-red-500/20 focus:border-red-500 text-sm appearance-none cursor-pointer"
            >
              <option value="">Sort By...</option>
              <option value="totalScore">Total Score</option>
              <option value="appliedDate">Date Applied</option>
              {selectedJob !== 'all' && <option value="skillsMatch">Skills Match</option>}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 w-full lg:w-auto justify-end">
          <button
            onClick={() => onSortChange(sortBy)}
            title="Toggle Sort Direction"
            className="p-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-600 transition-colors bg-white"
          >
            {sortDirection === 'asc' ? <ArrowUpDown className="w-5 h-5" /> : <ArrowUpDown className="w-5 h-5 rotate-180" />}
          </button>

          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all text-sm font-medium whitespace-nowrap ${showAdvanced || activeFiltersCount > (searchTerm ? 1 : 0) + (selectedJob !== 'all' ? 1 : 0)
              ? 'bg-red-50 border-red-200 text-red-700'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span>Filters</span>
            {activeFiltersCount > 0 && (
              <span className="bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full ml-1">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvanced && (
        <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-in slide-in-from-top-2 duration-200">
          {/* Skills Filter */}
          <div className="lg:col-span-2 space-y-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Skills</label>
            <div className="relative">
              <Code className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Type skill & press Enter..."
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSkill();
                  }
                }}
                className="pl-9 pr-14 py-2 w-full border border-gray-200 rounded-lg text-sm focus:ring-red-500 focus:border-red-500"
              />
              <button
                onClick={handleAddSkill}
                className="absolute right-1.5 top-1.5 bottom-1.5 px-3 bg-gray-900 text-white rounded-md text-xs font-medium hover:bg-gray-800"
              >
                Add
              </button>
            </div>
            {selectedSkills.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedSkills.map(skill => (
                  <span key={skill} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100">
                    {skill}
                    <button onClick={() => handleRemoveSkill(skill)} className="ml-1.5 hover:text-red-900"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Experience */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Experience</label>
            <select
              value={experienceLevel}
              onChange={(e) => setExperienceLevel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-red-500 focus:border-red-500 bg-white"
            >
              <option value="all">Any Experience</option>
              <option value="intern">Intern</option>
              <option value="junior">Junior (0-2y)</option>
              <option value="mid">Mid-Level (2-5y)</option>
              <option value="senior">Senior (5y+)</option>
            </select>
          </div>

          {/* Verification & Date */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Status & Date</label>
            <div className="flex gap-2">
              <select
                value={String(verificationStatus)}
                onChange={(e) => setVerificationStatus(e.target.value === 'all' ? 'all' : e.target.value === 'true')}
                className="w-1/2 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
              >
                <option value="all">Any Status</option>
                <option value="true">Verified Only</option>
                <option value="false">Unverified</option>
              </select>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-1/2 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
              >
                <option value="all">Any Date</option>
                <option value="7days">Last 7d</option>
                <option value="30days">Last 30d</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Footer / Clear Filters */}
      {activeFiltersCount > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
          <span className="text-gray-500">
            Found <b>{candidatesCount}</b> candidates matching your criteria
          </span>
          <button onClick={handleClearFilters} className="text-red-600 hover:text-red-700 font-medium text-sm flex items-center hover:underline">
            <X className="w-4 h-4 mr-1" /> Clear all filters
          </button>
        </div>
      )}
    </div>
  );
};

export default CandidateFilters;

